import React, { useState, useRef, Component } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { ChevronDownIcon, ClockIcon, PlayCircleIcon } from 'lucide-react';
const modules = [{
  week: '1-2',
  title: 'Design Foundations & UX Principles',
  duration: '8 hours',
  lessons: 12,
  topics: ['Design thinking methodology', 'User research techniques', 'Information architecture', 'Wireframing essentials']
}, {
  week: '3-4',
  title: 'Figma Mastery',
  duration: '10 hours',
  lessons: 16,
  topics: ['Advanced components & variants', 'Auto-layout deep dive', 'Design systems creation', 'Prototyping & interactions']
}, {
  week: '5',
  title: 'Visual Design & UI Components',
  duration: '8 hours',
  lessons: 14,
  topics: ['Typography & color theory', 'Visual hierarchy', 'UI component design', 'Icon & illustration basics']
}, {
  week: '6',
  title: 'Design Systems & Variables',
  duration: '7 hours',
  lessons: 11,
  topics: ['Building a design system', 'Figma variables & tokens', 'Component architecture', 'Documentation & handoff']
}, {
  week: '7-8',
  title: 'AI First Designer',
  duration: '6 hours',
  lessons: 10,
  topics: ['Figma to Claude Code', 'Midjourney for designers', 'ChatGPT for UX writing', 'Automating repetitive tasks']
}, {
  week: '9-10',
  title: 'Framer & No-Code Development',
  duration: '10 hours',
  lessons: 14,
  topics: ['Framer fundamentals', 'Advanced animations', 'CMS & dynamic content', 'Publishing & optimization']
}, {
  week: '11-12',
  title: 'Portfolio & Career Prep',
  duration: '6 hours',
  lessons: 8,
  topics: ['Portfolio case studies', 'Resume optimization', 'Interview preparation', 'Salary negotiation']
}];
export function CurriculumSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const [expandedIndex, setExpandedIndex] = useState<number | null>(0);
  return <section id="curriculum" className="py-24 bg-dark-900 relative" data-id="element-48">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref} data-id="element-49">
        {/* Section Header */}
        <div className="text-center mb-16" data-id="element-50">
          <motion.span initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5
        }} className="inline-block text-brand-yellow text-sm font-semibold tracking-wider uppercase mb-4" data-id="element-51">
            12-Week Curriculum
          </motion.span>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="text-4xl sm:text-5xl font-bold text-white mb-6" data-id="element-52">
            A proven path to
            <br data-id="element-53" />
            <span className="text-gradient-yellow" data-id="element-54">design excellence</span>
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="text-lg text-gray-400 max-w-2xl mx-auto" data-id="element-55">
            48+ hours of content, 74 lessons, and 8 portfolio-ready projects.
            Everything you need to become a professional product designer.
          </motion.p>
        </div>

        {/* Stats */}

        {/* Curriculum Accordion */}
        <div className="max-w-3xl mx-auto space-y-4" data-id="element-56">
          {modules.map((module, index) => <motion.div key={module.title} initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.1 * index
        }} className="rounded-xl border border-white/10 overflow-hidden bg-dark-800/30" data-id="element-57">
              <button onClick={() => setExpandedIndex(expandedIndex === index ? null : index)} className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-white/5 transition-colors" data-id="element-58">
                <div className="flex items-center gap-4" data-id="element-59">
                  <span className="flex-shrink-0 w-16 h-16 rounded-xl bg-brand-yellow/10 flex items-center justify-center" data-id="element-60">
                    <span className="text-brand-yellow font-bold" data-id="element-61">
                      W{module.week}
                    </span>
                  </span>
                  <div data-id="element-62">
                    <h3 className="text-lg font-semibold text-white" data-id="element-63">
                      {module.title}
                    </h3>
                    <div className="flex items-center gap-4 mt-1 text-sm text-gray-500" data-id="element-64">
                      <span className="flex items-center gap-1" data-id="element-65">
                        <ClockIcon className="w-4 h-4" data-id="element-66" />
                        {module.duration}
                      </span>
                      <span className="flex items-center gap-1" data-id="element-67">
                        <PlayCircleIcon className="w-4 h-4" data-id="element-68" />
                        {module.lessons} lessons
                      </span>
                    </div>
                  </div>
                </div>
                <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${expandedIndex === index ? 'rotate-180' : ''}`} data-id="element-69" />
              </button>

              {expandedIndex === index && <motion.div initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.3
          }} className="px-6 pb-5" data-id="element-70">
                  <div className="pl-20 border-l border-white/10 ml-8" data-id="element-71">
                    <ul className="space-y-3" data-id="element-72">
                      {module.topics.map(topic => <li key={topic} className="flex items-center gap-3 text-gray-400" data-id="element-73">
                          <span className="w-1.5 h-1.5 rounded-full bg-brand-yellow" data-id="element-74" />
                          {topic}
                        </li>)}
                    </ul>
                  </div>
                </motion.div>}
            </motion.div>)}
        </div>
      </div>
    </section>;
}